import { useTranslation } from 'react-i18next';
import { DateTime as DT } from 'luxon';
import { RECURRENT_WEEKDAYS, type RecurrentData } from '@econtractor/types';

interface RecurrentSummaryProps {
    data: RecurrentData | null;
    allowOverlap: boolean;
    active: boolean;
    cancelled: boolean;
    showAllowOverlap: boolean;
    hiddenCount?: number;
}

const RecurrentSummary = ({
    data,
    allowOverlap,
    active,
    cancelled,
    showAllowOverlap,
    hiddenCount = 0,
}: RecurrentSummaryProps) => {
    const { t } = useTranslation(['Tasks']);
    if (!data) {
        return <div className="task-card-empty">{t('recurrent.empty')}</div>;
    }

    const weekdays = RECURRENT_WEEKDAYS.filter((day) => data.weekdays.includes(day))
        .map((day) => t(`recurrent.weekday.${day}`))
        .join(', ');

    const endLabel =
        data.endType === 'date' && data.endDate
            ? DT.fromMillis(data.endDate, { zone: 'local' }).toFormat('dd.MM.yyyy')
            : data.endType === 'count'
              ? String(data.endCount || '')
              : t('recurrent.endNever');

    const status = cancelled
        ? t('recurrent.statusStopped')
        : active
          ? t('recurrent.statusActive')
          : t('recurrent.statusDone');
    const statusClass = cancelled ? 'text-danger' : active ? 'text-success' : 'text-muted';

    return (
        <div className="task-card-panel">
            <div className={`fw-medium mb-2 ${statusClass}`}>{status}</div>
            <dl className="task-recurrent-meta">
                <div className="task-recurrent-meta__row">
                    <dt className="text-muted mb-0">{t('recurrent.frequency')}</dt>
                    <dd className="task-recurrent-meta__value text-body mb-0">
                        {t('recurrent.summary', {
                            interval: data.interval,
                            frequency: t(`recurrent.freq.${data.frequency}`),
                        })}
                    </dd>
                </div>
                {data.frequency === 'week' && weekdays ? (
                    <div className="task-recurrent-meta__row">
                        <dt className="text-muted mb-0">{t('recurrent.weekdays')}</dt>
                        <dd className="task-recurrent-meta__value text-body mb-0">{weekdays}</dd>
                    </div>
                ) : null}
                {data.frequency === 'month' && data.monthDay ? (
                    <div className="task-recurrent-meta__row">
                        <dt className="text-muted mb-0">{t('recurrent.monthDay')}</dt>
                        <dd className="task-recurrent-meta__value text-body mb-0">{data.monthDay}</dd>
                    </div>
                ) : null}
                <div className="task-recurrent-meta__row">
                    <dt className="text-muted mb-0">{t('recurrent.end')}</dt>
                    <dd className="task-recurrent-meta__value text-body mb-0">{endLabel}</dd>
                </div>
                {showAllowOverlap ? (
                    <div className="task-recurrent-meta__row">
                        <dt className="text-muted mb-0">{t('recurrent.allowOverlap')}</dt>
                        <dd className="task-recurrent-meta__value text-body mb-0">
                            {allowOverlap ? t('recurrent.yes') : t('recurrent.no')}
                        </dd>
                    </div>
                ) : null}
            </dl>
            {hiddenCount > 0 ? (
                <div className="text-muted mt-2">{t('recurrent.hiddenCount', { count: hiddenCount })}</div>
            ) : null}
        </div>
    );
};

export default RecurrentSummary;
